import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import {
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material'
import { BackspaceMajor, SearchMinor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { GridColumnsSelectItem } from './GridColumnsSelectItem'
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridColDef,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro'

export interface GridColumnsSelectContentProps {
  columnsConfig: Array<GridColDef>
  columnVisibilityModel: GridColumnVisibilityModel
  lockedColumns?: Array<string>
  onToggleColumnVisibility(field: string): void
  onColumnVisibilityModelChange(model: GridColumnVisibilityModel): void
}

const GridColumnsSelectContentComponent = ({
  columnsConfig,
  columnVisibilityModel,
  lockedColumns = [],
  onToggleColumnVisibility,
  onColumnVisibilityModelChange,
}: GridColumnsSelectContentProps) => {
  const [searchQuery, setSearchQuery] = useState('')

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }, [])

  const handleSearchClear = useCallback(() => {
    setSearchQuery('')
  }, [])

  const manageableColumns = useMemo(() => {
    return columnsConfig.filter((column) => {
      return column.field !== 'actions' && column.field !== GRID_CHECKBOX_SELECTION_FIELD
    })
  }, [columnsConfig])

  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) {
      return manageableColumns
    }

    const lowerQuery = searchQuery.toLowerCase()

    return manageableColumns.filter((column) => {
      return (column.headerName ?? column.field).toLowerCase().includes(lowerQuery)
    })
  }, [manageableColumns, searchQuery])

  const visibleCount = useMemo(() => {
    return manageableColumns.filter((column) => {
      if (lockedColumns.includes(column.field)) {
        return true
      }

      return columnVisibilityModel[column.field] !== false
    }).length
  }, [manageableColumns, lockedColumns, columnVisibilityModel])

  const allVisible = visibleCount === manageableColumns.length

  const handleToggleAll = useCallback(() => {
    const newVisible = !allVisible

    const newModel = manageableColumns.reduce<GridColumnVisibilityModel>((model, column) => {
      if (lockedColumns.includes(column.field)) {
        model[column.field] = true
      } else {
        model[column.field] = newVisible
      }

      return model
    }, {})

    onColumnVisibilityModelChange(newModel)
  }, [manageableColumns, lockedColumns, allVisible, onColumnVisibilityModelChange])

  return (
    <div css={tw`flex flex-col w-[300px] min-w-[240px] max-w-full`}>
      <Typography fontWeight={600} variant="body2" css={tw`px-4 pt-3 pb-2 block`}>
        Manage columns
      </Typography>
      <div css={tw`px-3 pb-2`}>
        <TextField
          size="small"
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearchChange}
          autoFocus
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon css={tw`w-4 h-4 text-gray-400`}>
                  <SearchMinor />
                </SvgIcon>
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  css={tw`w-6 h-6 mr-[-6px] bg-transparent hover:bg-transparent`}
                  onClick={handleSearchClear}
                  size="small"
                  disableRipple
                >
                  <SvgIcon css={tw`w-4 h-4 text-[#8C8AA0]`}>
                    <BackspaceMajor />
                  </SvgIcon>
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #EDEDED',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #EDEDED',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '2px solid #7B61FF',
              },
            },
          }}
          css={tw`bg-white`}
        />
      </div>
      <Divider />
      <List disablePadding css={tw`py-1 max-h-[320px] overflow-y-auto`}>
        {filteredColumns.length > 0 ? (
          filteredColumns.map((column) => {
            const isLocked = lockedColumns.includes(column.field)
            const isVisible = isLocked ? true : columnVisibilityModel[column.field] !== false

            return (
              <GridColumnsSelectItem
                key={column.field}
                value={column.field}
                label={column.headerName ?? column.field}
                isVisible={isVisible}
                isLocked={isLocked}
                onToggleVisibility={onToggleColumnVisibility}
              />
            )
          })
        ) : (
          <Typography
            variant="body2"
            color={colors.GRAY_MID_COOL}
            css={tw`px-4 py-3 text-center block`}
          >
            No results found
          </Typography>
        )}
      </List>
      {!searchQuery && (
        <Fragment>
          <Divider />
          <ListItem
            css={tw`w-full h-10 px-2 cursor-pointer`}
            disablePadding
            onClick={handleToggleAll}
          >
            <Checkbox
              size="small"
              checked={allVisible}
              indeterminate={visibleCount > 0 && !allVisible}
              onChange={handleToggleAll}
              css={tw`p-1 mr-1`}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation()
              }}
              sx={{
                color: 'black',
                '&.Mui-checked': { color: 'black' },
                '&.MuiCheckbox-indeterminate': { color: 'black' },
              }}
            />
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={500} color={colors.GRAY_COOL_GRAY}>
                  Show/Hide All
                </Typography>
              }
            />
          </ListItem>
        </Fragment>
      )}
    </div>
  )
}

export const GridColumnsSelectContent = memo(GridColumnsSelectContentComponent)
